// Detalle expandido de un archivo: vista previa, TODOS sus usos (con botón
// para abrir al dueño), advertencias y metadatos completos. «Abrir archivo» (round 2026-10-28)
// abre el archivo en sí con el mismo criterio que el resto de la pantalla (aperturaArchivo.ts).
//
// Un video se reproduce con <VideoPlayer> (regla del proyecto: ningún <video> pelado). Aquí el
// administrador INSPECCIONA el archivo: abre siempre en HD (el original) y, si el servidor mandó la
// versión liviana, puede mirar esa con el selector de abajo. Con el selector propio no depende de
// la calidad que él haya elegido antes para ver videos (la preferencia del reproductor).
import React, { useState } from "react";
import { Alert, Button, Descriptions, Segmented, Space, Tag, Tooltip, Typography } from "antd";
import {
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LinkOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import VideoPlayer from "../../../Components/VideoPlayer";
import { formatPropertyNames } from "../../../utils/propertyDisplay";
import type { Archivo } from "../../../services/almacenamiento.api";
import {
  desglosePeso,
  etiquetaFecha,
  etiquetaTipoArchivo,
  formatPeso,
  formatPorcentaje,
  porcentaje,
  textoDesglosePeso,
  urlDeDescarga,
} from "../almacenamientoUtils";
import { aperturaDeArchivo, textoAccionAbrir } from "../aperturaArchivo";
import { iconoDeArchivo } from "./ArchivoMiniatura";
import SubidoPorCell from "./SubidoPorCell";

const { Text } = Typography;

interface Props {
  archivo: Archivo;
  /** Navega a la ruta del dueño (la página resuelve el router). */
  onAbrir: (ruta: string) => void;
  /**
   * Abre el ARCHIVO en sí, según su tipo (imagen / video en un visor, PDF en una pestaña, Office y
   * demás se descargan). No se muestra si no hay nada que abrir (referencia rota, etc.).
   */
  onAbrirArchivo: (archivo: Archivo) => void;
}

const VistaPrevia: React.FC<{ archivo: Archivo }> = ({ archivo }) => {
  if (archivo.estado === "roto") {
    return (
      <Alert
        type="error"
        showIcon
        message="El archivo ya no existe en el disco"
        description="Solo queda la referencia en el registro que lo usaba."
      />
    );
  }
  if (archivo.soloVersionLiviana) {
    return (
      <div className="alm-preview-doc">
        <span className="alm-preview-doc-icono">{iconoDeArchivo(archivo)}</span>
        <Text strong>Versión liviana sin video</Text>
        <Text type="secondary">
          Es la copia liviana de un video que ya no existe, así que no hay nada que reproducir.
        </Text>
      </div>
    );
  }
  if (!archivo.url && !archivo.thumbUrl) {
    return <Text type="secondary">Sin vista previa disponible.</Text>;
  }
  if (archivo.tipo === "image") {
    return (
      <a href={archivo.url || undefined} target="_blank" rel="noopener noreferrer">
        <img
          className="alm-preview-img"
          src={archivo.url || archivo.thumbUrl || undefined}
          alt={`Vista previa de ${archivo.nombre}`}
          loading="lazy"
        />
      </a>
    );
  }
  if (archivo.tipo === "video" && archivo.url) {
    return <VistaPreviaVideo archivo={archivo} url={archivo.url} />;
  }
  return (
    <div className="alm-preview-doc">
      <span className="alm-preview-doc-icono">{iconoDeArchivo(archivo)}</span>
      <Text type="secondary">Documento: no hay vista previa en esta página.</Text>
    </div>
  );
};

type VersionVideo = "hd" | "liviana";

/** Reproductor del video con, si hay versión liviana, un selector para inspeccionar cada una. */
const VistaPreviaVideo: React.FC<{ archivo: Archivo; url: string }> = ({ archivo, url }) => {
  const [version, setVersion] = useState<VersionVideo>("hd");
  const previewUrl = archivo.previewUrl || null;
  // Sin versión liviana (o si dejó de llegar) siempre es el original.
  const efectiva: VersionVideo = previewUrl ? version : "hd";
  const { versionLiviana } = desglosePeso(archivo);

  return (
    <div className="alm-preview-video-bloque">
      {previewUrl && (
        <Segmented<VersionVideo>
          className="alm-preview-version"
          size="small"
          value={efectiva}
          onChange={(v) => setVersion(v)}
          options={[
            { value: "hd", label: "Original (HD)" },
            {
              value: "liviana",
              label: versionLiviana > 0 ? `Versión liviana (${formatPeso(versionLiviana)})` : "Versión liviana",
            },
          ]}
          aria-label="Qué versión del video reproducir"
          data-testid="alm-video-version"
        />
      )}
      {/* `key`: al cambiar de versión se monta un reproductor nuevo (un <video> no reinicia su src solo). */}
      <VideoPlayer
        key={efectiva}
        src={efectiva === "liviana" && previewUrl ? previewUrl : url}
        className="alm-preview-video"
        wrapperClassName="alm-preview-video-envoltorio"
        preload="none"
        playsInline
        // El detalle ya trae su propio selector de versión y su botón de descarga.
        descargable={false}
        altoMaximo="260px"
        poster={archivo.thumbUrl || undefined}
        aria-label={`Vista previa de ${archivo.nombre}${efectiva === "liviana" ? " (versión liviana)" : ""}`}
        testId="alm-video"
      />
    </div>
  );
};

const ArchivoDetalle: React.FC<Props> = ({ archivo, onAbrir, onAbrirArchivo }) => {
  const fecha = etiquetaFecha(archivo.fecha, archivo.fechaOrigen);
  const desglose = desglosePeso(archivo);
  const esVideoPropio = archivo.tipo === "video" && !archivo.soloVersionLiviana;
  const origenFecha =
    archivo.fechaOrigen === "registrada"
      ? "registrada por el sistema"
      : archivo.fechaOrigen === "deducida"
        ? "deducida del nombre interno (estimada)"
        : "desconocida";

  return (
    <div className="alm-detalle">
      <div className="alm-detalle-previa">
        <VistaPrevia archivo={archivo} />
        {aperturaDeArchivo(archivo).modo !== "ninguno" && archivo.url && (
          <Space wrap style={{ marginTop: 8 }}>
            <Tooltip title={textoAccionAbrir(aperturaDeArchivo(archivo))}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                aria-label={`Abrir archivo ${archivo.nombre}`}
                onClick={() => onAbrirArchivo(archivo)}
              >
                Abrir archivo
              </Button>
            </Tooltip>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              href={urlDeDescarga(archivo.url)}
              download={archivo.nombre}
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar
            </Button>
          </Space>
        )}
      </div>

      <div className="alm-detalle-info">
        <Text strong>
          {archivo.usos.length === 0
            ? "No lo usa ningún registro"
            : `Se usa en ${archivo.usos.length} ${archivo.usos.length === 1 ? "lugar" : "lugares"}`}
        </Text>

        {archivo.usos.length === 0 && archivo.estado === "huerfano" && (
          <Alert
            style={{ marginTop: 8 }}
            type="info"
            showIcon
            message="Archivo huérfano"
            description={
              archivo.soloVersionLiviana
                ? "Es la copia liviana de un video que ya no existe: ocupa espacio y nadie la usa. Eliminarla no cambia nada."
                : "Ocupa espacio pero nadie lo usa: eliminarlo no cambia nada en el sistema."
            }
          />
        )}

        <ul className="alm-usos">
          {archivo.usos.map((uso) => {
            const props = formatPropertyNames(uso.propiedades);
            return (
              <li key={`${uso.fuente}-${uso.filaId}`} className="alm-uso">
                <div className="alm-uso-cabecera">
                  <Tag color="purple">{uso.etiquetaModulo}</Tag>
                  <Text strong className="alm-uso-titulo">
                    {uso.titulo || `Registro #${uso.filaId}`}
                  </Text>
                  {uso.ruta && (
                    <Button
                      size="small"
                      type="link"
                      icon={<LinkOutlined />}
                      onClick={() => onAbrir(uso.ruta as string)}
                      aria-label={`Abrir ${uso.etiquetaModulo}: ${uso.titulo}`}
                    >
                      Abrir
                    </Button>
                  )}
                </div>
                <div className="alm-uso-meta">
                  {props.map((p) => (
                    <Tag key={p}>{p}</Tag>
                  ))}
                  {uso.visibleParaCliente ? (
                    <Tag icon={<EyeOutlined />} color="blue">
                      Lo ve el cliente
                    </Tag>
                  ) : (
                    <Tag icon={<EyeInvisibleOutlined />}>Solo administración</Tag>
                  )}
                </div>
                {uso.sensible && (
                  <Alert
                    className="alm-uso-sensible"
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message={uso.sensible}
                  />
                )}
              </li>
            );
          })}
        </ul>

        <Descriptions
          className="alm-meta"
          size="small"
          bordered
          column={{ xs: 1, sm: 1, md: 2 }}
          title="Detalles del archivo"
        >
          <Descriptions.Item label="Nombre">{archivo.nombre}</Descriptions.Item>
          <Descriptions.Item label="Tipo">
            {etiquetaTipoArchivo(archivo)} {archivo.mime ? `(${archivo.mime})` : ""}
          </Descriptions.Item>
          <Descriptions.Item label="Peso total">
            {formatPeso(archivo.pesoBytes)}
          </Descriptions.Item>
          <Descriptions.Item label="Desglose">
            <span data-testid="alm-desglose-peso">{textoDesglosePeso(desglose)}</span>
          </Descriptions.Item>
          {esVideoPropio && (
            <Descriptions.Item label="Versión liviana">
              {desglose.versionLiviana > 0 ? (
                <span data-testid="alm-version-liviana">
                  Sí, pesa {formatPeso(desglose.versionLiviana)}
                  {desglose.archivo > 0
                    ? ` (${formatPorcentaje(porcentaje(desglose.versionLiviana, desglose.archivo))} del video)`
                    : ""}
                  . Es la que se reproduce por defecto para gastar menos datos.
                </span>
              ) : (
                <span data-testid="alm-version-liviana">
                  No tiene: se reproduce el original. Puede ser un video anterior a esta función,
                  de poco peso, que ya es de baja resolución o que todavía se está procesando.
                </span>
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Fecha de subida">
            {fecha.texto} <Text type="secondary">({origenFecha})</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Subido por">
            <SubidoPorCell subidoPor={archivo.subidoPor} size={22} />
          </Descriptions.Item>
          <Descriptions.Item label="Carpeta">
            <Text code>{archivo.carpeta}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Clave interna">
            <Text code className="alm-key">
              {archivo.key}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </div>
    </div>
  );
};

export default ArchivoDetalle;
